'use client';

import { Container } from '@vs-shop/ui/container';
import { Heading } from '@vs-shop/ui/heading';
import { Match, Option } from 'effect';
import { useIntl } from 'react-intl';

import {
  ContentGallery,
  ContentGalleryHeader,
  ContentGalleryImage,
  ContentGalleryItem,
  ContentGalleryLink,
  ContentGalleryNext,
  ContentGalleryPrevious,
  ContentGallerySteps,
  ContentGalleryTitle,
  ContentGalleryTrack,
} from '@vs-shop/commerce-ui/contentGallery';

import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';

import type { ImageCollectionBlock } from '../../../../effect/schema/openapi/backend';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';

type Props = {
  content: ImageCollectionBlock;
};

const NEW_TAB: { readonly target: '_blank' } = { target: '_blank' };

/** Where a photograph leads: only a link the editor actually filled in. */
const destinationOf = (link: string | null): Option.Option<string> =>
  Option.filter(Option.fromNullishOr(link), (href) => href.trim() !== '');

/** An `external` link opens a new tab; `internal` (or none chosen) stays in this one. */
const targetOf = (linkType: string | null): { readonly target?: '_blank' } =>
  Match.value(linkType).pipe(
    Match.when('external', () => NEW_TAB),
    Match.orElse(() => ({}))
  );

export default function ContentBlockImageCollection({ content }: Props) {
  const intl = useIntl();

  return (
    <ContentGallery>
      {/* BUTTONS */}
      <Container
        classNameWrapper="bg-background-intense"
        classNameInner="max-w-(--container-cms-base)"
      >
        <ContentGalleryHeader>
          <ContentGalleryTitle render={<Heading level={2} />}>
            {intl.formatMessage({
              id: 'contentBlock.imageGallery.heading',
              defaultMessage: 'Galerie',
            })}
          </ContentGalleryTitle>

          <ContentGallerySteps>
            <ContentGalleryPrevious />
            <ContentGalleryNext />
          </ContentGallerySteps>
        </ContentGalleryHeader>
      </Container>

      {/* IMAGES */}
      <ContentGalleryTrack>
        {content.images.map((image, idx) => {
          const imagePathAbstract = image.imageAbstract ?? null;

          if (imagePathAbstract === null) return null;

          const imagePath = resolveImageUrl(
            imagePathAbstract,
            ImageFilter.cms_page_content_images_collection_2x
          );

          const photograph = (
            <ContentGalleryImage src={imagePath} alt={image.alt ?? ''} width={573} height={430} />
          );

          return (
            <ContentGalleryItem key={idx}>
              {Option.match(destinationOf(image.link), {
                onNone: () => photograph,
                onSome: (href) => (
                  <ContentGalleryLink href={href} {...targetOf(image.linkType)}>
                    {photograph}
                  </ContentGalleryLink>
                ),
              })}
            </ContentGalleryItem>
          );
        })}
      </ContentGalleryTrack>
    </ContentGallery>
  );
}
